import { useState } from 'react';
import { Link } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Empty } from '@/components/ui/empty';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { DeviceIcon } from '@/components/art/LineArt';
import { ErrorState, Busy } from '@/components/kit';
import { Stars } from '@/components/inputs';
import { formatRelativeTime } from '@/utils/date';
import { cn } from '@/lib/utils';
import { GLOW, LABEL, WRAP } from '@/components/home/styles';
import { faqSchema, useJsonLd } from '@/shared/seo';
import { useT } from '@/shared/i18n';
import { H } from '@/shared/locales/home/keys';

const { eq, ne, and } = window.ApperSDK.CoreSDK;

const DEVICES = [
  { k: 'tv', n: H.smartTv, s: H.smartTvS },
  { k: 'stick', n: H.fire, s: H.fireS },
  { k: 'box', n: H.boxes, s: H.boxesS },
  { k: 'phone', n: H.phones, s: H.phonesS },
  { k: 'tablet', n: H.tablets, s: H.tabletsS },
  { k: 'laptop', n: H.computers, s: H.computersS },
];

const FAQS = [
  { q: H.q1, a: H.a1 },
  { q: H.q2, a: H.a2 },
  { q: H.q3, a: H.a3 },
  { q: H.q4, a: H.a4 },
  { q: H.q5, a: H.a5 },
  { q: H.q6, a: H.a6 },
  { q: H.q7, a: H.a7 },
  { q: H.q8, a: H.a8 },
  { q: H.q9, a: H.a9 },
  { q: H.q10, a: H.a10 },
  { q: H.q11, a: H.a11, link: { to: '/install', label: H.installLink } },
];

const REVIEW_FIELDS = ['Name', 'display_name_c', 'plan_c', 'rating_c', 'body_c', 'reply_c', 'hidden_c', 'CreatedOn'];
const PUBLIC_REVIEWS = () => and(eq('hidden_c', false), ne('plan_c', 'Banshee'));

export function Devices() {
  const { t } = useT();
  return (
    <section id="devices" className={cn(WRAP, 'scroll-mt-24 pt-36')}>
      <p className={LABEL}>{t(H.supported)}</p>
      <h2 className="mt-4 max-w-3xl text-[clamp(34px,5vw,60px)] font-light leading-[1.05]">
        {t(H.haunts)}
      </h2>
      <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-3">
        {DEVICES.map((d) => (
          <div key={d.n} className="group flex flex-col gap-5 bg-background p-6 transition-colors hover:bg-card md:p-8">
            <div className="text-primary transition-transform duration-(--transition-slow) group-hover:-translate-y-1">
              <DeviceIcon kind={d.k} />
            </div>
            <div>
              <p className="text-lg">{t(d.n)}</p>
              <p className="mt-1 text-sm text-muted-foreground">{t(d.s)}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export function Reviews() {
  const { t } = useT();
  const [limit, setLimit] = useState(6);

  const { data, loading, error, run } = useFetch(
    () =>
      sdk
        .table('reviews_c')
        .select(REVIEW_FIELDS)
        .where(PUBLIC_REVIEWS())
        .orderByDesc('CreatedOn')
        .limit(limit)
        .fetch()
        .then((r) => {
          if (!r.success) throw new Error(r.message);
          return { rows: r.data ?? [], total: r.total ?? 0 };
        }),
    [limit],
  );

  const { data: stats } = useFetch(
    () =>
      sdk
        .table('reviews_c')
        .select([{ field: 'Id' }])
        .aggregate([
          { id: 'count', fields: [{ field: 'Id', fn: 'Count', alias: 'count' }], where: PUBLIC_REVIEWS() },
          { id: 'avg', fields: [{ field: 'rating_c', fn: 'Avg', alias: 'count' }], where: PUBLIC_REVIEWS() },
        ])
        .limit(1)
        .fetch()
        .then((r) => {
          if (!r.success) throw new Error(r.message);
          const agg = (id) => {
            const b = r.aggregators?.find((a) => a.id === id);
            return Number(b?.data?.count ?? b?.count ?? b?.value ?? 0);
          };
          return { count: agg('count'), avg: agg('avg') };
        }),
    [],
  );

  const list = data?.rows ?? [];
  const total = data?.total ?? 0;
  const firstLoad = loading && data === undefined;

  return (
    <section id="reviews" className={cn(WRAP, 'scroll-mt-24 pt-36')}>
      <div className="grid gap-14 md:grid-cols-[1fr_1.6fr]">
        <div>
          <p className={LABEL}>{t('Reviews')}</p>
          <h2 className="mt-4 text-[clamp(34px,5vw,60px)] font-light leading-[1.05]">
            {t(H.testimonials)}
          </h2>
          {stats && stats.count > 0 && (
            <div className="mt-10">
              <p className={cn('text-[clamp(64px,8vw,96px)] font-light leading-none', GLOW)}>
                {stats.avg.toFixed(1)}
              </p>
              <div className="mt-4 flex items-center gap-3">
                <Stars value={stats.avg} size={20} />
                <span className="text-sm text-muted-foreground">
                  {t(H.reviewsCount, { count: stats.count })}
                </span>
              </div>
            </div>
          )}
          <p className="mt-8 max-w-xs text-sm text-muted-foreground">
            {t(H.leaveYours)}
          </p>
        </div>
        <div>
          {firstLoad && (
            <div className="space-y-8">
              {[0, 1, 2].map((i) => (
                <div key={i} className="space-y-3 border-t border-border pt-6">
                  <Skeleton className="h-4 w-28" />
                  <Skeleton className="h-5 w-2/3" />
                  <Skeleton className="h-4 w-full" />
                </div>
              ))}
            </div>
          )}
          {!firstLoad && error && <ErrorState message={error} onRetry={run} />}
          {!firstLoad && !error && list.length === 0 && (
            <Empty
              icon="MessageSquareQuote"
              title={t(H.noTestimonials)}
              description={t(H.firstReviews)}
            />
          )}
          {!firstLoad && !error && list.length > 0 && (
            <div aria-busy={loading} className={cn('transition-opacity', loading && 'opacity-60')}>
              {list.map((r) => (
                <article key={r.Id} className="border-t border-border py-7">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <Stars value={r.rating_c} />
                    <span className="text-xs text-muted-foreground">
                      {r.display_name_c || t(H.viewer)} · {r.plan_c} · {formatRelativeTime(r.CreatedOn)}
                    </span>
                  </div>
                  <h3 className="mt-4 text-xl">{r.Name}</h3>
                  <p className="mt-2 text-[15px] leading-7 text-muted-foreground">{r.body_c}</p>
                  {r.reply_c && (
                    <p className="mt-4 border-s-2 border-primary/60 ps-4 text-sm text-foreground/80">
                      <span className="text-primary">{t(H.replied)} </span>
                      {r.reply_c}
                    </p>
                  )}
                </article>
              ))}
              {total > list.length && (
                <Button variant="outline" disabled={loading} onClick={() => setLimit((n) => n + 6)}>
                  <Busy busy={loading} label={t(H.showMore)} busyLabel={t(H.loading)} />
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export function FAQ() {
  const { t } = useT();
  useJsonLd('faq', faqSchema(FAQS));

  return (
    <section id="faq" className={cn(WRAP, 'scroll-mt-24 pt-36')}>
      <div className="grid gap-12 md:grid-cols-[1fr_1.6fr]">
        <div>
          <p className={LABEL}>{t('FAQ')}</p>
          <h2 className="mt-4 text-[clamp(34px,5vw,60px)] font-light leading-[1.05]">{t(H.whispers)}</h2>
        </div>
        <Accordion type="single" collapsible defaultValue="faq-0" className="border-t border-border">
          {FAQS.map((f, i) => (
            <AccordionItem key={f.q} value={`faq-${i}`} className="border-border">
              <AccordionTrigger className="py-5 text-start text-lg font-normal hover:text-primary hover:no-underline">
                {t(f.q)}
              </AccordionTrigger>
              <AccordionContent className="max-w-2xl pb-6 text-[15px] leading-7 text-muted-foreground">
                {t(f.a)}
                {f.link && (
                  <Link to={f.link.to} className="mt-3 block text-primary hover:underline">
                    {t(f.link.label)} →
                  </Link>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
